import type {
  CampaignGiftDetailData,
  CampaignOrder,
  CampaignOrderProduct,
  CampaignGiftDetailRequest,
  CampaignGiftDetailResponse,
  CategoryTreeResponse,
  CreatorGiftingOverrideRequest as GeneratedCreatorGiftingOverrideRequest,
  CreatorGiftingOverrideResponse,
  GiftingCollectionCreateRequest,
  GiftingCollectionListItem,
  GiftingCollectionProductsGetResponse,
  GiftingCollectionProductsRequest,
  GiftingCollectionProductsSaveResponse,
  GiftingCollectionResponse,
  GiftingCollectionUpdateRequest,
  GiftingOrderUpdateRequest,
  OCAPIProductDetailResponse,
  ProductSearchRequest,
  ProductSearchResponse,
  SendGiftingInvitesRequest,
  _HydratedCollectionProduct,
} from '@dashhudson/api-client/creator/model';

import {
  getDeleteBrandsBrandIdGiftingCollectionsCollectionIdUrl,
  getGetBrandsBrandIdGiftingCampaignsCampaignIdCollectionsUrl,
  getGetBrandsBrandIdGiftingCampaignsCampaignIdGiftDetailUrl,
  getGetBrandsBrandIdGiftingCampaignsCampaignIdOrdersUrl,
  getGetBrandsBrandIdGiftingCollectionsCollectionIdProductsUrl,
  getGetBrandsBrandIdGiftingOcapiCategoriesUrl,
  getGetBrandsBrandIdGiftingOcapiProductsProductIdUrl,
  getPatchBrandsBrandIdGiftingCollectionsCollectionIdUrl,
  getPatchBrandsBrandIdGiftingOrdersOrderIdUrl,
  getPostBrandsBrandIdGiftingCollectionsUrl,
  getPostBrandsBrandIdGiftingInviteUrl,
  getPostBrandsBrandIdGiftingOcapiProductSearchUrl,
  getPostBrandsBrandIdGiftingOrdersApproveUrl,
  getPutBrandsBrandIdGiftingCampaignsCampaignIdCreatorOverridesCreatorIdUrl,
  getPutBrandsBrandIdGiftingCampaignsCampaignIdGiftDetailUrl,
  getPutBrandsBrandIdGiftingCollectionsCollectionIdProductsUrl,
} from '@dashhudson/api-client/creator/client';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.creatorsApiUrl, {
  camelizeKeys: true,
});

export type GiftDetail = CampaignGiftDetailData;
export interface CreatorGiftingOverrideRequest
  extends Omit<GeneratedCreatorGiftingOverrideRequest, 'budgetAmount'> {
  budgetAmount?: number | null;
}
export type GiftingCollection = GiftingCollectionListItem;
export type CampaignGiftOrder = CampaignOrder;
export type CampaignGiftOrderProduct = CampaignOrderProduct;
export type HydratedCollectionProduct = _HydratedCollectionProduct;

export const GIFT_ORDER_STATUS = {
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  PLACING: 'placing',
  ORDER_PLACED: 'order_placed',
  FAILED: 'failed',
} as const;

// The backend has a limit of 100 orders per approve request.
export const APPROVE_ORDERS_MAX = 100;

export const GIFT_ORDER_PRODUCT_STATUS = {
  OK: 'ok',
  OUT_OF_STOCK: 'out_of_stock',
  NOT_LIVE: 'not_live',
  DELETED: 'deleted',
} as const satisfies Record<string, NonNullable<CampaignGiftOrderProduct['status']>>;

export async function getGiftDetail(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: GiftDetail }>(
    getGetBrandsBrandIdGiftingCampaignsCampaignIdGiftDetailUrl(brandId, campaignId),
    config,
  );
}

export async function upsertGiftDetail(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  body: CampaignGiftDetailRequest,
  config: HttpRequestConfig = {},
) {
  return http.put<CampaignGiftDetailResponse>(
    getPutBrandsBrandIdGiftingCampaignsCampaignIdGiftDetailUrl(brandId, campaignId),
    body,
    config,
  );
}

export async function updateGiftDetail(
  { brandId, campaignId, ordersOpen }: { brandId: number; campaignId: number; ordersOpen: boolean },
  config: HttpRequestConfig = {},
) {
  return http.put<{ data: GiftDetail }>(
    getPutBrandsBrandIdGiftingCampaignsCampaignIdGiftDetailUrl(brandId, campaignId),
    { ordersOpen },
    config,
  );
}

export async function upsertCreatorGiftingOverride(
  { brandId, campaignId, creatorId }: { brandId: number; campaignId: number; creatorId: number },
  body: CreatorGiftingOverrideRequest,
  config: HttpRequestConfig = {},
) {
  return http.put<CreatorGiftingOverrideResponse>(
    getPutBrandsBrandIdGiftingCampaignsCampaignIdCreatorOverridesCreatorIdUrl(
      brandId,
      campaignId,
      creatorId,
    ),
    body,
    config,
  );
}

export async function getGiftingCollections(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: GiftingCollection[] }>(
    getGetBrandsBrandIdGiftingCampaignsCampaignIdCollectionsUrl(brandId, campaignId),
    config,
  );
}

export async function createGiftingCollection(
  { brandId }: { brandId: number },
  body: GiftingCollectionCreateRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<GiftingCollectionResponse>(
    getPostBrandsBrandIdGiftingCollectionsUrl(brandId),
    body,
    config,
  );
}

export async function updateGiftingCollection(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  body: GiftingCollectionUpdateRequest,
  config: HttpRequestConfig = {},
) {
  return http.patch<GiftingCollectionResponse>(
    getPatchBrandsBrandIdGiftingCollectionsCollectionIdUrl(brandId, collectionId),
    body,
    config,
  );
}

export async function getCampaignGiftOrders(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: CampaignGiftOrder[] }>(
    getGetBrandsBrandIdGiftingCampaignsCampaignIdOrdersUrl(brandId, campaignId),
    config,
  );
}

export async function sendGiftingInvites(
  { brandId }: { brandId: number },
  body: SendGiftingInvitesRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<void>(getPostBrandsBrandIdGiftingInviteUrl(brandId), body, config);
}

export async function approveGiftOrders(
  { brandId }: { brandId: number },
  { orderIds }: { orderIds: number[] },
  config: HttpRequestConfig = {},
) {
  return http.post<void>(
    getPostBrandsBrandIdGiftingOrdersApproveUrl(brandId),
    { orderIds },
    config,
  );
}

export async function updateGiftOrder(
  { brandId, orderId }: { brandId: number; orderId: number },
  body: GiftingOrderUpdateRequest,
  config: HttpRequestConfig = {},
) {
  return http.patch<void>(
    getPatchBrandsBrandIdGiftingOrdersOrderIdUrl(brandId, orderId),
    body,
    config,
  );
}

export async function deleteGiftingCollection(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(
    getDeleteBrandsBrandIdGiftingCollectionsCollectionIdUrl(brandId, collectionId),
    config,
  );
}

export async function getGiftingCollectionProducts(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<GiftingCollectionProductsGetResponse>(
    getGetBrandsBrandIdGiftingCollectionsCollectionIdProductsUrl(brandId, collectionId),
    config,
  );
}

export async function saveGiftingCollectionProducts(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  body: GiftingCollectionProductsRequest,
  config: HttpRequestConfig = {},
) {
  return http.put<GiftingCollectionProductsSaveResponse>(
    getPutBrandsBrandIdGiftingCollectionsCollectionIdProductsUrl(brandId, collectionId),
    body,
    config,
  );
}

export async function getGiftingCategories(
  { brandId }: { brandId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<CategoryTreeResponse>(
    getGetBrandsBrandIdGiftingOcapiCategoriesUrl(brandId),
    config,
  );
}

export async function searchGiftingProducts(
  { brandId }: { brandId: number },
  body: ProductSearchRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<ProductSearchResponse>(
    getPostBrandsBrandIdGiftingOcapiProductSearchUrl(brandId),
    body,
    config,
  );
}

export async function getGiftingProduct(
  { brandId, productId }: { brandId: number; productId: string },
  config: HttpRequestConfig = {},
) {
  return http.get<OCAPIProductDetailResponse>(
    getGetBrandsBrandIdGiftingOcapiProductsProductIdUrl(brandId, productId),
    config,
  );
}
